import { cloneDeep, findIndex, isEmpty, negate, pickBy, some } from 'lodash';

export const toggleGlobalCheckbox = (selectedItems: Array<any> = [], data: any): Array<any> => {
  const { id } = data;

  let existingArr: any = cloneDeep(selectedItems);
  let existingId = findIndex(existingArr, function (item: any) {
    return item.id === id;
  });

  if (existingId !== -1) {
    existingArr.splice(existingId, 1);
    return existingArr;
  }
  existingArr.push({ ...data });
  return existingArr;
};

export const toggleOrgCheckbox = (
  selectedOrgItems: Array<any> = [],
  data: any,
  dependencyKey: string,
  orgData?: any
): Array<any> => {
  let { id } = data;
  let organizationId = orgData?.id;

  if (data?.data?.orgId?.length && !organizationId) {
    organizationId = data?.data?.orgId;
  }

  if (data?.organizationId !== organizationId) {
    data.organizationId = organizationId;
  }

  if (orgData?.name) {
    data.organizationName = orgData?.name;
  }

  let existingArr: any = cloneDeep(selectedOrgItems);
  let orgIndex = findIndex(existingArr, function (item: any) {
    return item.id === organizationId;
  });

  if (orgIndex !== -1) {
    let existingId = findIndex(
      existingArr[orgIndex]?.dependentArtifactConfigurations?.[dependencyKey],
      function (item: any) {
        return item.id === id;
      }
    );
    if (existingId !== -1) {
      existingArr[orgIndex]?.dependentArtifactConfigurations?.[dependencyKey].splice(existingId, 1);
      return existingArr;
    } else {
      existingArr[orgIndex]?.dependentArtifactConfigurations?.[dependencyKey].push({
        ...data
      });
      return existingArr;
    }
  } else {
    let payload = {
      id: organizationId,
      dependentArtifactConfigurations: {
        [dependencyKey]: [
          {
            ...data
          }
        ]
      }
    };
    existingArr.push(payload);
    return existingArr;
  }
};

export const toggleOrgAllArtifacts = (
  selectedOrgItems: Array<any> = [],
  organizationData: any,
  dependencyKey: string,
  label: string = ''
): Array<any> => {
  let { id: organizationId, name = '' } = organizationData || {};

  let existingArr: any = cloneDeep(selectedOrgItems);
  let orgIndex = findIndex(existingArr, function (item: any) {
    return item.id === organizationId;
  });

  if (orgIndex !== -1) {
    let existingId = findIndex(
      existingArr[orgIndex]?.dependentArtifactConfigurations?.[dependencyKey],
      function (item: any) {
        return item.id === 'ALL';
      }
    );

    if (existingId !== -1) {
      existingArr[orgIndex].dependentArtifactConfigurations[dependencyKey] = [];
      return existingArr;
    } else {
      existingArr[orgIndex].dependentArtifactConfigurations[dependencyKey] = [
        {
          id: 'ALL',
          organizationName: name,
          organizationId,
          name: label
        }
      ];
      return existingArr;
    }
  } else {
    let payload = {
      id: organizationId,
      dependentArtifactConfigurations: {
        [dependencyKey]: [
          {
            id: 'ALL',
            organizationName: name,
            organizationId,
            name: label
          }
        ]
      }
    };
    existingArr.push(payload);
    return existingArr;
  }
};

export const isGlobalSelected = (selectedItems: Array<any> = [], data: any): boolean => {
  const { id } = data;

  return (
    findIndex(selectedItems, function (item: any) {
      return item.id === id;
    }) !== -1
  );
};

export const isOrgSpecificSelected = (
  selectedOrgItems: Array<any> = [],
  data: any,
  dependencyKey: string,
  orgId?: string
): boolean => {
  let organizationId = orgId ? orgId : data?.organizationId || data?.data?.orgId || '';
  let { id } = data;

  let orgIndex = findIndex(selectedOrgItems, function (item: any) {
    return item.id === organizationId;
  });
  if (orgIndex !== -1) {
    if (data === 'ALL') {
      return (
        selectedOrgItems[orgIndex]?.dependentArtifactConfigurations?.[dependencyKey]?.[0]?.id ===
        'ALL'
      );
    } else {
      return (
        findIndex(
          selectedOrgItems[orgIndex]?.dependentArtifactConfigurations?.[dependencyKey],
          function (item: any) {
            return item.id === id;
          }
        ) !== -1
      );
    }
  } else {
    return false;
  }
};

export const toggleOrgOverrideCheckbox = (
  manifestData: Array<any>,
  data: any,
  dependencyKey: string,
  orgId?: string
) => {
  let organizationId = orgId
    ? orgId
    : data?.organizationId
    ? data?.organizationId
    : data?.data?.orgId || '';

  let { id } = data;

  let existingArr: any = cloneDeep(manifestData);
  let existingId = findIndex(existingArr, function (item: any) {
    return item.id === organizationId;
  });

  let itemId = findIndex(
    existingArr[existingId]?.dependentArtifactConfigurations?.[dependencyKey],
    function (item: any) {
      return item.id === id;
    }
  );

  if (itemId !== -1) {
    if (
      existingArr[existingId]?.dependentArtifactConfigurations?.[dependencyKey]?.[itemId]?.overWrite
    ) {
      delete existingArr[existingId].dependentArtifactConfigurations?.[dependencyKey]?.[itemId]
        .overWrite;
    } else {
      existingArr[existingId].dependentArtifactConfigurations[dependencyKey][itemId].overWrite =
        true;
    }

    return existingArr;
  }
};

export const toggleGlobalOverrideCheckbox = (manifestData: any, data: any) => {
  const { id } = data;
  let existingArr: any = cloneDeep(manifestData);
  let existingId = findIndex(existingArr, function (item: any) {
    return item.id === id;
  });

  if (existingId !== -1) {
    if (existingArr[existingId]?.overWrite) {
      delete existingArr[existingId].overWrite;
    } else {
      existingArr[existingId].overWrite = true;
    }
  }

  return existingArr;
};

export const cleanupOrganizationManifest = (manifest: any) => {
  return manifest.filter((org: any) => {
    if (org?.dependentArtifactConfigurations) {
      const { dependentArtifactConfigurations } = org;

      const hasNonEmptyArray = some(dependentArtifactConfigurations, (config) => !isEmpty(config));

      if (!hasNonEmptyArray) {
        return false;
      }

      org.dependentArtifactConfigurations = pickBy(
        dependentArtifactConfigurations,
        negate(isEmpty)
      );

      return true;
    }
    return true;
  });
};

export const removeOrgData = (
  selectedOrgItems: Array<any>,
  data: any,
  dependencyKey: string,
  orgId?: string
) => {
  let organizationId = orgId ? orgId : data?.organizationId || '';
  let { id } = data;

  if (data?.data?.orgId?.length && !organizationId) {
    organizationId = data?.data?.orgId;
  }
  let existingArr: any = cloneDeep(selectedOrgItems);
  let orgIndex = findIndex(existingArr, function (item: any) {
    return item.id === organizationId;
  });
  if (orgIndex !== -1) {
    let existingId = findIndex(
      existingArr[orgIndex]?.dependentArtifactConfigurations?.[dependencyKey],
      function (item: any) {
        return item.id === id;
      }
    );

    if (existingId !== -1) {
      existingArr[orgIndex]?.dependentArtifactConfigurations?.[dependencyKey].splice(existingId, 1);
      if (
        isEmpty(existingArr[orgIndex]?.dependentArtifactConfigurations) ||
        isEmpty(existingArr[orgIndex]?.dependentArtifactConfigurations?.[dependencyKey])
      ) {
        existingArr.splice(orgIndex, 1);
      }

      return existingArr;
    }
  }
  return existingArr;
};
